"use client"

import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import type {
  PrintOrder,
  OrderDetails,
  Representation,
  Person,
  Exhibitor,
  Participation,
} from "./types" 
import {
  FaInfoCircle,
  FaTag,
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaDollarSign,
  FaBriefcase,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa"

interface OrderDetailsModalProps {
  isOpen: boolean
  toggle: () => void
  order: PrintOrder | null
}

/**
 * Modal component to display detailed information about a specific PrintOrder.
 */
export default function OrderDetailsModal({ isOpen, toggle, order }: OrderDetailsModalProps) {
  if (!order) {
    return null // Don't render if no order is provided
  }

  // Safely access nested data
  const orderDetail: OrderDetails | undefined = order.order_details?.[0]
  const representation: Representation | undefined = orderDetail?.representation_details
  const person: Person | undefined = representation?.person_details
  const exhibitor: Exhibitor | undefined = representation?.exhibitor_details
  const participation: Participation | undefined = order.participation_details

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="bg-info text-white">
        <FaInfoCircle className="me-2" /> Order Details: {order.order_number}
      </ModalHeader>
      <ModalBody className="p-4">
        <h5 className="mb-3 text-primary">
          <FaTag className="me-2" /> Order Information
        </h5>
        <div className="row mb-4">
          <div className="col-md-6 mb-2">
            <strong>Order Number:</strong> {order.order_number}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Status:</strong>{" "}
            <span className={`badge bg-${getStatusBadgeColor(order.status)}`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>
          <div className="col-md-6 mb-2">
            <strong>Requested On:</strong> {new Date(order.created_at).toLocaleDateString()}
          </div>
         
          {order.reason && (
            <div className="col-12 mb-2">
              <strong>Reason:</strong> {order.reason}
            </div>
          )}
        </div>

        

        {participation && (
          <>
            <h5 className="mb-3 text-primary">
              <FaCalendarAlt className="me-2" /> Participation Details
            </h5>
            <div className="row">
              <div className="col-md-6 mb-2">
                <strong>Exhibition:</strong> {participation.exhibition_title || "N/A"}
              </div>
             
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// Helper function (can be moved to a utility file if used elsewhere)
const getStatusBadgeColor = (status: PrintOrder["status"]) => {
  switch (status) {
    case "pending":
      return "warning"
    case "paid":
      return "info"
    case "approved":
      return "success"
    case "rejected":
      return "danger"
    case "printed":
      return "primary"
    default:
      return "secondary"
  }
}
